import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClockIcon, PlusSmIcon } from '@heroicons/react/outline';

import omit from 'lodash.omit';
import classNames from 'classnames';
import debounce from 'lodash.debounce';

import {
  ActivityType,
  ProviderCollection,
  ActivityListDataFragment,
  useArchiveActivityMutation,
  useProviderActivitiesQuery,
  useProviderCollectionsQuery,
  useArchiveActivitiesMutation,
  useDuplicateActivityMutation,
  useEditActivityTitleMutation,
  useAddActivityToProviderCollectionMutation,
  useStandardAssessmentsQuery,
} from '../../generated/graphql';

import { useAuth } from '../../contexts/AuthContext';
import { isProviderUserOwnerOrAdminAuthorized } from '../../lib/auth';

import { ColumnOrder } from '../types/tables';
import { ProviderActivityType } from '../types/activity';

import {
  ProviderActivityObject,
  getProviderActivityTitle,
  formatProviderActivityForFollowUp,
  isProviderActivityANonCustomAssessment,
} from '../lib/providerActivity';
import { isProviderOnlyAssessment } from '../lib/assessments';

import { pluralize } from '../lib/copy';
import { ContentType } from '../lib/followUp';
import { DEFAULT_TRANSITION_DURATION } from '../lib/animation';

import useFixPage from '../hooks/useFixPage';

import AddToLibraryMenu, {
  AddToLibraryMenuProps,
} from '../components/AddToLibraryMenu';

import {
  sortOptions,
  sortActivities,
  sortCollections,
  doesActivityMeetFilters,
  doesCollectionMeetFilters,
  getSelectedProviderActivitiesList,
} from '../components/Library/helpers';

import ActionButton from '../components/ActionButton';
import BottomButtons from '../components/BottomButtons';
import Button from '../components/Button';
import TableContainer from '../components/TableContainer';
import AddPDFModal from '../components/Modals/AddPDFModal';
import AddLinkModal from '../components/Modals/AddLinkModal';
import SearchAndFilters from '../components/SearchAndFilters';
import LibraryTable from '../components/Library/LibraryTable';
import StandardAssessmentsTable from '../components/Library/StandardAssessmentsTable';
import AddImageModal from '../components/Modals/AddImageModal';
import AddAudioModal from '../components/Modals/AddAudioModal';
import LibraryActivityModal from '../components/Modals/LibraryActivityModal';
import PageContainer from '../components/Containers/PageContainer';
import { customToast } from '../components/ToastAlert/customToast';
import FileSettingsModal from '../components/Modals/FileSettingsModal';
import AddOrEditTextModal from '../components/Modals/AddOrEditTextModal';
import RenameActivityModal from '../components/Modals/RenameActivityModal';
import LibraryHeaderButtons from '../components/Library/LibraryHeaderButtons';
import CuratedProviderCollectionGrid from '../components/CuratedProviderCollectionGrid';
import ProviderActivitySlideover from '../components/Library/ProviderActivitySlideover';
import ConfirmArchiveActivityModal from '../components/Modals/ConfirmArchiveActivityModal';
import ConfirmArchiveActivitiesModal from '../components/Modals/ConfirmArchiveActivitiesModal';
import ConfirmDeleteProviderCollectionModal from '../components/Library/Modals/ConfirmDeleteProviderCollectionModal';
import CreateOrUpdateProviderCollectionModal from '../components/Library/Modals/CreateOrUpdateProviderCollectionModal';
import LibraryTableContainer from '../components/Library/LibraryTableContainer';

const Library = () => {
  useFixPage();
  const navigate = useNavigate();
  const { showUpgradeBanner, authedProviderUser } = useAuth();

  const location = useLocation();
  // Get providerCollectionId from location state or URL search params
  const providerCollectionIdFromState = (
    location.state as { providerCollectionId?: string }
  )?.providerCollectionId;
  const searchParams = new URLSearchParams(location.search);
  const providerCollectionIdFromUrl = searchParams.get('collection');
  const providerCollectionId =
    providerCollectionIdFromState || providerCollectionIdFromUrl || undefined;

  const [selectedProviderActivity, setSelectedProviderActivity] =
    useState<ActivityListDataFragment>();

  const [isProviderActivitySlideoverOpen, setIsProviderActivitySlideoverOpen] =
    useState(false);

  const [isAddPDFModalOpen, setIsAddPDFModalOpen] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isAddAudioModalOpen, setIsAddAudioModalOpen] = useState(false);
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState(false);
  const [isAddOrEditTextModalOpen, setIsAddOrEditTextModalOpen] =
    useState(false);
  const [isFileSettingsModalOpen, setIsFileSettingsModalOpen] = useState(false);

  const [
    isCreateOrUpdateProviderCollectionModalOpen,
    setIsCreateOrUpdateProviderCollectionModalOpen,
  ] = useState(false);

  const openCreateOrUpdateProviderCollectionModal = () =>
    setIsCreateOrUpdateProviderCollectionModalOpen(true);

  const [isCreatingNewProviderCollection, setIsCreatingNewProviderCollection] =
    useState(true);

  const [isArchivingResource, setIsArchivingResource] = useState(false);
  const [isArchivingResources, setIsArchivingResources] = useState(false);
  const [showActivityActionsMenu, setShowActivityActionsMenu] = useState(false);

  const [
    isConfirmDeleteCollectionModalOpen,
    setIsConfirmDeleteCollectionModalOpen,
  ] = useState(false);
  const [
    isConfirmArchiveActivityModalOpen,
    setIsConfirmArchiveActivityModalOpen,
  ] = useState(false);
  const [
    isConfirmArchiveActivitiesModalOpen,
    setIsConfirmArchiveActivitiesModalOpen,
  ] = useState(false);

  const [isRenameActivityModalOpen, setIsRenameActivityModalOpen] =
    useState(false);
  const [isRenamingActivity, setIsRenamingActivity] = useState(false);

  const [isLibraryActivityModalOpen, setIsLibraryActivityModalOpen] =
    useState(false);

  // Determine current view from pathname
  const pathname = location.pathname;
  const isMyResources = pathname === '/library';
  const isStandardAssessments = pathname === '/library/standard-assessments';
  const isDiscover = pathname === '/library/discover';

  const [archiveActivityMutation] = useArchiveActivityMutation();
  const [archiveActivitiesMutation] = useArchiveActivitiesMutation();
  const [duplicateActivityMutation] = useDuplicateActivityMutation();
  const [editActivityTitleMutation] = useEditActivityTitleMutation();
  const [addActivityToProviderCollection] =
    useAddActivityToProviderCollectionMutation();

  const {
    data: providerActivitiesData,
    error: providerActivitiesError,
    loading: providerActivitiesLoading,
    refetch: refetchProviderActivities,
  } = useProviderActivitiesQuery({
    errorPolicy: 'all', // Allow partial data - some activities may have errors but list should still load
  });

  const providerActivities = providerActivitiesData?.providerActivities;

  const {
    data: providerCollectionsData,
    error: providerCollectionsError,
    refetch: refetchProviderCollections,
  } = useProviderCollectionsQuery();

  const {
    data: standardAssessmentsData,
    loading: standardAssessmentsLoading,
    refetch: refetchStandardAssessments,
  } = useStandardAssessmentsQuery();

  const providerCollections = providerCollectionsData?.providerCollections;

  const [selectedProviderCollection, setSelectedProviderCollection] = useState<
    ProviderCollection | undefined
  >();

  // Auto-selects a provider collection if ID sent in location state
  useEffect(() => {
    if (providerCollectionId && providerCollections) {
      setSelectedProviderCollection(
        providerCollections.find(
          (providerCollection) =>
            providerCollection.id === providerCollectionId,
        ),
      );
    }
  }, [
    providerCollectionId,
    providerCollections,
    setSelectedProviderCollection,
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermValue, setSearchTermValue] = useState('');

  const [contentTypeFilter, setContentTypeFilter] =
    useState<ContentType | null>(null);

  const [activityOrder, setActivityOrder] = useState<ColumnOrder>(
    sortOptions.newestCreated,
  );

  const debouncedSearch = useCallback(
    debounce(
      (e) => setSearchTerm(e.target.value.toLowerCase().trim()),
      DEFAULT_TRANSITION_DURATION,
    ),
    [],
  );

  const openAddOrEditTextModal = () => setIsAddOrEditTextModalOpen(true);

  const hasContentTypeFilter = contentTypeFilter !== null;

  const providerActivitiesList = sortActivities(
    providerActivities?.filter((providerActivity) =>
      doesActivityMeetFilters(providerActivity, {
        searchTerm,
        contentTypeFilter,
        selectedProviderCollection,
      }),
    ),
    activityOrder !== null ? activityOrder : sortOptions.newestCreated,
  );

  const sortedProviderCollections = sortCollections(
    providerCollections?.filter((providerCollection) =>
      doesCollectionMeetFilters(providerCollection, {
        searchTerm,
        contentTypeFilter,
        selectedProviderCollection,
        allProviderActivities: providerActivities,
      }),
    ),
    activityOrder !== null ? activityOrder : sortOptions.newestCreated,
  );

  useEffect(() => {
    // Only show error if we have an error AND no data (critical failure)
    // With errorPolicy: 'all', we may have errors but still get partial data
    if (
      providerActivitiesError &&
      !providerActivitiesData?.providerActivities?.length
    ) {
      customToast.error('Unable to fetch library items.');
    }
  }, [providerActivitiesError, providerActivitiesData]);

  useEffect(() => {
    if (providerCollectionsError) {
      customToast.error('Unable to fetch library folders.');
    }
  }, [providerCollectionsError]);

  const clearSearchTerm = () => {
    setSearchTermValue('');
    setSearchTerm('');
  };

  const showEmptyTableFilterState = Boolean(
    !providerActivitiesLoading &&
      providerActivitiesList?.length === 0 &&
      sortedProviderCollections?.length === 0,
  );

  const archiveProviderResource = async () => {
    if (!selectedProviderActivity) return;

    try {
      setIsArchivingResource(true);
      await archiveActivityMutation({
        variables: {
          activityId: selectedProviderActivity.id,
          activityType: selectedProviderActivity.activityType,
        },
      });
      customToast.success('Resource deleted.');
      await refetchProviderActivities?.();
    } catch (error) {
      customToast.error('Unable to delete resource.');
    } finally {
      setIsArchivingResource(false);
      setIsConfirmArchiveActivityModalOpen(false);
      setIsProviderActivitySlideoverOpen(false);
    }
  };

  const duplicateActivity = async () => {
    if (!selectedProviderActivity) return;

    try {
      await duplicateActivityMutation({
        variables: {
          input: {
            activityId: selectedProviderActivity.id,
            activityType: selectedProviderActivity.activityType,
          },
        },
      });
      customToast.success('Resource duplicated.');
      await refetchProviderActivities?.();
    } catch (error) {
      customToast.error(
        'Unable to duplicate resource. Please try again later.',
      );
    }
  };

  const renameActivity = async (newTitle: string) => {
    if (!selectedProviderActivity) return;

    try {
      setIsRenamingActivity(true);
      await editActivityTitleMutation({
        variables: {
          input: {
            activityId: selectedProviderActivity.id,
            activityType: selectedProviderActivity.activityType,
            title: newTitle,
          },
        },
      });
      customToast.success('Resource renamed.');
      setIsRenamingActivity(false);
      setIsRenameActivityModalOpen(false);
      await refetchProviderActivities?.();
    } catch (error) {
      setIsRenamingActivity(false);
      setIsRenameActivityModalOpen(false);
      customToast.error('Unable to rename resource.');
    }
  };

  const [
    addSelectedProviderActivityToCollection,
    setAddSelectedProviderActivityToCollection,
  ] = useState(false);

  const createProviderCollection = (
    addSelectedProviderActivityToCollection?: boolean,
  ) => {
    setIsCreatingNewProviderCollection(true);
    openCreateOrUpdateProviderCollectionModal();
    addSelectedProviderActivityToCollection &&
      setAddSelectedProviderActivityToCollection(true);
  };

  const renameProviderCollection = () => {
    setIsCreatingNewProviderCollection(false);
    openCreateOrUpdateProviderCollectionModal();
  };

  const clearFilterTerm = () => setContentTypeFilter(null);

  const clearSearchAndFilters = () => {
    clearSearchTerm();
    clearFilterTerm();
  };

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTermValue(e.target.value);
    debouncedSearch(e);
  };

  const hasEmptyProviderCollection = Boolean(
    selectedProviderCollection &&
      providerActivitiesList?.length === 0 &&
      !hasContentTypeFilter &&
      !searchTerm,
  );

  const [
    selectedProviderActivitiesObject,
    setSelectedProviderActivitiesObject,
  ] = useState<ProviderActivityObject>({});

  const selectedProviderActivitiesList = getSelectedProviderActivitiesList(
    selectedProviderActivitiesObject,
  );

  const clearSelectedProviderActivities = () =>
    setSelectedProviderActivitiesObject({});

  const showBottomButtons = Boolean(selectedProviderActivitiesList.length);

  const selectedProviderActivitiesOmitNonCustomAssessments =
    selectedProviderActivitiesList.filter(
      (activity) => !isProviderActivityANonCustomAssessment(activity),
    );

  const bottomButtonActions = [
    {
      title: `Delete ${pluralize(
        selectedProviderActivitiesOmitNonCustomAssessments.length,
        'resource',
        'resources',
      )} `,
      theme: 'destructive' as const,
      onClick: () => {
        setIsConfirmArchiveActivitiesModalOpen(true);
      },
      disabled: selectedProviderActivitiesOmitNonCustomAssessments.length === 0,
    },
    {
      title: `Send ${pluralize(
        selectedProviderActivitiesList.length,
        'resource',
        'resources',
      )}`,
      theme: 'secondary' as const,
      onClick: () => {
        sendProviderActivitiesInFollowUp(selectedProviderActivitiesList);
      },
    },
  ];

  const archiveProviderResources = async (
    providerActivities: ActivityListDataFragment[],
  ): Promise<void> => {
    try {
      setIsArchivingResources(true);
      const { data } = await archiveActivitiesMutation({
        variables: {
          input: {
            activities: providerActivities
              .filter(
                (activity) => !isProviderActivityANonCustomAssessment(activity),
              )
              .map((activity) => ({
                activityId: activity.id,
                activityType: activity.activityType,
              })),
          },
        },
      });
      if (data?.archiveActivities) {
        customToast.success(
          `${pluralize(
            data.archiveActivities.length,
            'resource',
            'resources',
          )} deleted.`,
        );
        await refetchProviderActivities?.();
      }
    } catch (error) {
      customToast.error('Unable to delete resources.');
    } finally {
      setIsArchivingResources(false);
      setIsConfirmArchiveActivityModalOpen(false);
      setIsProviderActivitySlideoverOpen(false);
      clearSelectedProviderActivities();
    }
  };

  const sendProviderActivitiesInFollowUp = (
    providerActivities: ActivityListDataFragment[],
  ): void => {
    // Filter out provider-only assessments - they cannot be sent via follow-ups
    const followUpEligibleActivities = providerActivities.filter(
      (providerActivity) => {
        if (providerActivity.__typename === 'ActivityAssessment') {
          return !isProviderOnlyAssessment(providerActivity.assessment);
        }
        return true;
      },
    );

    if (followUpEligibleActivities.length !== providerActivities.length) {
      customToast.warning(
        'Some forms are provider-only and cannot be sent via follow-ups. They have been excluded.',
      );
    }

    const formattedProviderActivities = followUpEligibleActivities?.map(
      (providerActivity, index) =>
        formatProviderActivityForFollowUp(providerActivity, index),
    );

    navigate(`/follow-ups/new`, {
      state: {
        initialActivities: formattedProviderActivities,
      },
    });
  };

  // Bind callbacks to the Add to Library button, so we don't have to pass them
  // all over the place
  const BoundAddToLibrary: FC<AddToLibraryMenuProps> = (props) => {
    return (
      <AddToLibraryMenu
        containerClassName="z-[40]"
        onClickAddLink={() => setIsAddLinkModalOpen(true)}
        onClickAddImage={() => setIsAddImageModalOpen(true)}
        onClickAddPDF={() => setIsAddPDFModalOpen(true)}
        onClickAddAudio={() => setIsAddAudioModalOpen(true)}
        onClickAddForm={(formType) =>
          navigate('/library/forms/new', {
            state: {
              formType,
              ...(selectedProviderCollection && {
                fromProviderCollectionId: selectedProviderCollection.id,
              }),
              fromPage: 'library',
            },
          })
        }
        onClickAddText={openAddOrEditTextModal}
        {...props}
      />
    );
  };

  const refetchAll = async () => {
    await Promise.all([
      refetchProviderCollections(),
      refetchProviderActivities(),
      refetchStandardAssessments(),
    ]);
  };

  const onActivityAdded = (activityType: ActivityType) => {
    return async (activityId: string) => {
      if (selectedProviderCollection) {
        await addActivityToProviderCollection({
          variables: {
            input: {
              activityId,
              activityType,
              providerCollectionId: selectedProviderCollection.id,
            },
          },
        });

        await refetchAll();
      }
    };
  };

  return (
    <>
      <PageContainer
        noPadding
        show={
          Boolean(providerActivities) ||
          Boolean(standardAssessmentsData?.standardAssessments)
        }
        loading={
          providerActivitiesLoading ||
          (isStandardAssessments && standardAssessmentsLoading)
        }
        onClick={() => setShowActivityActionsMenu(false)}
        containerClassName={classNames(
          'pb-0',
          showUpgradeBanner
            ? 'h-[calc(100vh-var(--top-nav-height)-var(--upgrade-banner-height))] overflow-y-hidden'
            : 'h-[calc(100vh-var(--top-nav-height))] overflow-y-hidden',
        )}
      >
        <>
          <div className="sticky top-0 z-10 flex w-full flex-row items-center justify-between gap-x-10 bg-white px-20 pt-[40px] pb-[40px] text-neutral-150">
            <div className="mr-4 flex flex-col gap-y-3">
              <h1 className="mb-1 font-serif text-subtitle text-neutral-150">
                Library
              </h1>
              <div className="text-neutral-120 max-w-xl text-body">
                {isStandardAssessments
                  ? 'Browse and send standard assessments to your clients. These assessments are pre-configured and ready to use.'
                  : isDiscover
                  ? 'Discover curated collections and resources from the community. Add them to your library to share with your clients.'
                  : 'Manage your resources and folders. Add content to your library and organize it into folders.'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LibraryHeaderButtons />
            </div>
          </div>
          {isMyResources && (
            <div className="relative z-[50] mb-6 flex h-[48px] w-full flex-row items-center justify-between px-20">
              <SearchAndFilters
                searchTermValue={searchTermValue}
                clearSearchTerm={clearSearchTerm}
                clearFilterTerm={clearFilterTerm}
                onChangeSearch={onChangeSearch}
                contentTypeFilter={contentTypeFilter}
                setContentTypeFilter={setContentTypeFilter}
              />
              <div className="flex flex-row items-center gap-3">
                {isProviderUserOwnerOrAdminAuthorized(authedProviderUser) && (
                  <ActionButton
                    size="small"
                    aria-label="Library activity"
                    title="Library activity"
                    onClick={() => setIsLibraryActivityModalOpen(true)}
                  >
                    <ClockIcon className="h-4 w-4 text-neutral-125" />
                  </ActionButton>
                )}
                {!selectedProviderCollection && (
                  <Button
                    IconComponent={PlusSmIcon}
                    iconPosition="left"
                    theme="secondary"
                    size="small"
                    title="Create folder"
                    onClick={() => createProviderCollection()}
                  />
                )}
                <BoundAddToLibrary />
              </div>
            </div>
          )}
          <TableContainer containerClassName="px-20">
            <LibraryTableContainer
              libraryContent={
                <>
                  {isStandardAssessments ? (
                    <>
                      <StandardAssessmentsTable
                        assessments={
                          standardAssessmentsData?.standardAssessments || []
                        }
                        setSelectedProviderActivity={
                          setSelectedProviderActivity
                        }
                        setIsProviderActivitySlideoverOpen={
                          setIsProviderActivitySlideoverOpen
                        }
                        sendProviderActivitiesInFollowUp={
                          sendProviderActivitiesInFollowUp
                        }
                        showEmptyTableFilterState={
                          !standardAssessmentsLoading &&
                          standardAssessmentsData?.standardAssessments
                            ?.length === 0
                        }
                      />
                    </>
                  ) : isDiscover ? (
                    <div className="w-full p-5">
                      <CuratedProviderCollectionGrid
                        numCols="three"
                        onCollectionAdded={async () => {
                          await refetchAll();
                        }}
                      />
                    </div>
                  ) : (
                    <LibraryTable
                      activityOrder={activityOrder}
                      providerActivities={providerActivitiesList}
                      selectedProviderCollection={selectedProviderCollection}
                      setActivityOrder={setActivityOrder}
                      renameProviderCollection={renameProviderCollection}
                      setIsConfirmDeleteCollectionModalOpen={
                        setIsConfirmDeleteCollectionModalOpen
                      }
                      setIsConfirmArchiveActivityModalOpen={
                        setIsConfirmArchiveActivityModalOpen
                      }
                      duplicateActivity={duplicateActivity}
                      setIsRenameActivityModalOpen={
                        setIsRenameActivityModalOpen
                      }
                      setIsFileSettingsModalOpen={setIsFileSettingsModalOpen}
                      setSelectedProviderActivity={setSelectedProviderActivity}
                      setSelectedProviderCollection={
                        setSelectedProviderCollection
                      }
                      setIsProviderActivitySlideoverOpen={
                        setIsProviderActivitySlideoverOpen
                      }
                      refetchProviderActivities={refetchProviderActivities}
                      showEmptyTableFilterState={showEmptyTableFilterState}
                      hasEmptyProviderCollection={hasEmptyProviderCollection}
                      clearSearchAndFilters={clearSearchAndFilters}
                      createProviderCollection={createProviderCollection}
                      sortedProviderCollections={sortedProviderCollections}
                      allProviderCollections={providerCollections}
                      openAddOrEditTextModal={openAddOrEditTextModal}
                      selectedProviderActivity={selectedProviderActivity}
                      showActivityActionsMenu={showActivityActionsMenu}
                      setShowActivityActionsMenu={setShowActivityActionsMenu}
                      sendProviderActivitiesInFollowUp={
                        sendProviderActivitiesInFollowUp
                      }
                      selectedProviderActivitiesObject={
                        selectedProviderActivitiesObject
                      }
                      setSelectedProviderActivitiesObject={
                        setSelectedProviderActivitiesObject
                      }
                      selectedProviderActivitiesList={
                        selectedProviderActivitiesList
                      }
                      contentTypeFilter={contentTypeFilter}
                      searchTerm={searchTerm}
                      BoundAddToLibrary={BoundAddToLibrary}
                      allProviderActivities={providerActivities}
                    />
                  )}
                </>
              }
            />
          </TableContainer>
        </>
      </PageContainer>

      <AddLinkModal
        isModalOpen={isAddLinkModalOpen}
        setClosed={() => setIsAddLinkModalOpen(false)}
        refetchProviderActivities={refetchProviderActivities}
        onAdded={onActivityAdded(ActivityType.Link)}
      />
      <AddPDFModal
        isModalOpen={isAddPDFModalOpen}
        setClosed={() => setIsAddPDFModalOpen(false)}
        refetchProviderActivities={refetchProviderActivities}
        onAdded={onActivityAdded(ActivityType.Pdf)}
      />
      <AddImageModal
        isModalOpen={isAddImageModalOpen}
        setClosed={() => setIsAddImageModalOpen(false)}
        refetchProviderActivities={refetchProviderActivities}
        onAdded={onActivityAdded(ActivityType.Image)}
      />
      <AddAudioModal
        isModalOpen={isAddAudioModalOpen}
        setClosed={() => setIsAddAudioModalOpen(false)}
        refetchProviderActivities={refetchProviderActivities}
        onAdded={onActivityAdded(ActivityType.Audio)}
      />
      <AddOrEditTextModal
        isModalOpen={isAddOrEditTextModalOpen}
        setClosed={() => setIsAddOrEditTextModalOpen(false)}
        clearModalData={() => setSelectedProviderActivity(undefined)}
        refetchProviderActivities={refetchProviderActivities}
        selectedActivityText={
          selectedProviderActivity?.__typename === ProviderActivityType.Text
            ? selectedProviderActivity
            : undefined
        }
        onAdded={onActivityAdded(ActivityType.Text)}
      />
      <ProviderActivitySlideover
        isOpen={isProviderActivitySlideoverOpen}
        onClose={() => setIsProviderActivitySlideoverOpen(false)}
        selectedProviderActivity={selectedProviderActivity}
        setIsConfirmArchiveActivityModalOpen={
          setIsConfirmArchiveActivityModalOpen
        }
        selectedProviderCollection={selectedProviderCollection}
      />
      <CreateOrUpdateProviderCollectionModal
        isModalOpen={isCreateOrUpdateProviderCollectionModalOpen}
        setClosed={() => setIsCreateOrUpdateProviderCollectionModalOpen(false)}
        refetchProviderCollections={refetchProviderCollections}
        isCreatingNewProviderCollection={isCreatingNewProviderCollection}
        selectedProviderCollection={selectedProviderCollection}
        setSelectedProviderCollection={setSelectedProviderCollection}
        selectedProviderActivity={selectedProviderActivity}
        addSelectedProviderActivityToCollection={
          addSelectedProviderActivityToCollection
        }
        setAddSelectedProviderActivityToCollection={
          setAddSelectedProviderActivityToCollection
        }
        refetchProviderActivities={refetchProviderActivities}
      />
      <ConfirmArchiveActivityModal
        isModalOpen={isConfirmArchiveActivityModalOpen}
        setClosed={() => setIsConfirmArchiveActivityModalOpen(false)}
        onConfirm={async () => {
          await archiveProviderResource();

          if (selectedProviderActivity) {
            const remainingSelectedProviderActivities = omit(
              selectedProviderActivitiesObject,
              [selectedProviderActivity.id],
            );

            setSelectedProviderActivitiesObject?.(
              remainingSelectedProviderActivities,
            );
          }
        }}
        isArchivingResource={isArchivingResource}
        activityType={selectedProviderActivity?.activityType}
      />
      <ConfirmArchiveActivitiesModal
        isModalOpen={isConfirmArchiveActivitiesModalOpen}
        setClosed={() => setIsConfirmArchiveActivitiesModalOpen(false)}
        isArchivingResources={isArchivingResources}
        selectedProviderActivitiesListLength={
          selectedProviderActivitiesOmitNonCustomAssessments.length
        }
        onConfirm={async () => {
          await archiveProviderResources(selectedProviderActivitiesList);
          setSelectedProviderActivitiesObject?.({});
          setIsConfirmArchiveActivitiesModalOpen(false);
        }}
      />
      <RenameActivityModal
        isModalOpen={isRenameActivityModalOpen}
        setClosed={() => setIsRenameActivityModalOpen(false)}
        clearModalData={() => setSelectedProviderActivity(undefined)}
        onSave={renameActivity}
        isRenamingActivity={isRenamingActivity}
        activityName={
          selectedProviderActivity
            ? getProviderActivityTitle(selectedProviderActivity) ?? ''
            : ''
        }
      />
      <FileSettingsModal
        isModalOpen={isFileSettingsModalOpen}
        setClosed={() => setIsFileSettingsModalOpen(false)}
        activityPdf={
          selectedProviderActivity?.__typename === ProviderActivityType.PDF
            ? selectedProviderActivity
            : undefined
        }
        onUpdated={() => refetchProviderActivities()}
      />
      <ConfirmDeleteProviderCollectionModal
        isModalOpen={isConfirmDeleteCollectionModalOpen}
        setClosed={() => setIsConfirmDeleteCollectionModalOpen(false)}
        selectedProviderCollection={selectedProviderCollection}
        onDeleted={refetchAll}
        setSelectedProviderCollection={setSelectedProviderCollection}
      />
      <BottomButtons
        isVisible={showBottomButtons}
        actions={bottomButtonActions}
      />
      {isProviderUserOwnerOrAdminAuthorized(authedProviderUser) && (
        <LibraryActivityModal
          isOpen={isLibraryActivityModalOpen}
          setClosed={() => setIsLibraryActivityModalOpen(false)}
        />
      )}
    </>
  );
};

export default Library;
